/**
 * Page Exit Tracking and Session Duration Utilities
 * Uses visibilitychange for reliable page exit detection
 */

import { safeCapture } from '../lib/analytics';
import type { PageExitProperties, SessionData, PageViewData } from '../lib/analyticsTypes';

class PageTrackingManager {
  private pageStartTime: number = Date.now();
  private sessionData: SessionData | null = null;
  private currentPageData: PageViewData | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private scrollDepthThresholds = [25, 50, 75, 90, 100];
  private maxScrollDepth = 0;
  private interactionCount = 0;
  private isVisible = true;
  private hasSetupListeners = false;

  constructor() {
    this.initializeSession();
  }

  private initializeSession(): void {
    this.sessionData = {
      session_id: this.generateSessionId(),
      start_time: Date.now(),
      page_views: 0,
      interactions: 0,
      last_activity: Date.now()
    };

    // Store session data in sessionStorage for persistence across page reloads
    sessionStorage.setItem('analytics_session', JSON.stringify(this.sessionData));
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private updateLastActivity(): void {
    if (this.sessionData) {
      this.sessionData.last_activity = Date.now();
      sessionStorage.setItem('analytics_session', JSON.stringify(this.sessionData));
    }
  }

  private getScrollDepth(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    if (documentHeight <= 0) return 100;
    
    const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);
    return Math.min(100, Math.max(0, scrollPercentage));
  }

  private trackScrollDepth(): void {
    const currentDepth = this.getScrollDepth();
    this.maxScrollDepth = Math.max(this.maxScrollDepth, currentDepth);

    // Track scroll milestones
    for (const threshold of this.scrollDepthThresholds) {
      if (currentDepth >= threshold && this.maxScrollDepth >= threshold) {
        safeCapture('scroll_depth_reached', {
          scroll_depth_percentage: threshold,
          page_path: window.location.pathname,
          timestamp: Date.now()
        });
        
        // Remove tracked threshold to avoid duplicate events
        this.scrollDepthThresholds = this.scrollDepthThresholds.filter(t => t !== threshold);
      }
    }
  }

  private setupEventListeners(): void {
    if (this.hasSetupListeners) return;
    this.hasSetupListeners = true;

    // Primary: visibilitychange (most reliable)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Fallback: pagehide (for browsers with limited visibilitychange support)
    window.addEventListener('pagehide', this.handlePageHide.bind(this));

    // Track user interactions
    ['click', 'keydown', 'scroll', 'mousemove'].forEach(eventType => {
      document.addEventListener(eventType, this.handleUserInteraction.bind(this), { passive: true });
    });

    // Track scroll depth
    window.addEventListener('scroll', this.trackScrollDepth.bind(this), { passive: true });

    // Optional: beforeunload (unreliable but better than nothing)
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }

  private handleVisibilityChange(): void {
    this.isVisible = document.visibilityState === 'visible';
    
    if (document.visibilityState === 'hidden') {
      this.trackPageExit('visibility_change');
    } else if (document.visibilityState === 'visible') {
      // Page became visible again - reset timer
      this.pageStartTime = Date.now();
      this.updateLastActivity();
    }
  }

  private handlePageHide(event: PageTransitionEvent): void {
    if (!event.persisted) {
      this.trackPageExit('pagehide');
    }
  }

  private handleBeforeUnload(): void {
    // Use beforeunload as last resort - keep it lightweight
    this.trackPageExit('beforeunload');
  }

  private handleUserInteraction(): void {
    this.interactionCount++;
    this.updateLastActivity();
  }

  private trackPageExit(exitMethod: PageExitProperties['exit_method']): void {
    const timeOnPage = Date.now() - this.pageStartTime;
    
    // Only track if user spent meaningful time on page (>1 second)
    if (timeOnPage < 1000) return;

    const properties: PageExitProperties = {
      page_path: window.location.pathname,
      time_on_page_seconds: Math.round(timeOnPage / 1000),
      scroll_depth_percentage: this.maxScrollDepth,
      exit_method: exitMethod,
      interactions_count: this.interactionCount,
      timestamp: new Date().toISOString()
    };

    // Always use PostHog SDK's safeCapture which handles authentication correctly
    // The SDK will use sendBeacon internally when appropriate
    safeCapture('page_exit_detailed', properties);

    // Update session data
    if (this.sessionData) {
      this.sessionData.interactions += this.interactionCount;
      sessionStorage.setItem('analytics_session', JSON.stringify(this.sessionData));
    }
  }

  private startHeartbeat(): void {
    // Send periodic heartbeat to track session duration
    this.heartbeatInterval = setInterval(() => {
      if (this.isVisible && this.sessionData) {
        const sessionDuration = Date.now() - this.sessionData.start_time;
        
        safeCapture('session_heartbeat', {
          session_duration_minutes: Math.round(sessionDuration / 60000),
          page_path: window.location.pathname,
          page_views: this.sessionData.page_views,
          total_interactions: this.sessionData.interactions + this.interactionCount,
          last_activity_minutes_ago: Math.round((Date.now() - this.sessionData.last_activity) / 60000)
        });

        // Track extended sessions (>10 minutes)
        if (sessionDuration > 10 * 60 * 1000) {
          safeCapture('session_extended', {
            session_duration_minutes: Math.round(sessionDuration / 60000),
            page_path: window.location.pathname
          });
        }
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Public methods
  public startPageTracking(pagePath: string, pageName?: string): void {
    this.currentPageData = {
      page_path: pagePath,
      page_name: pageName || pagePath,
      entry_time: Date.now(),
      scroll_depth: 0,
      interactions: 0
    };

    this.pageStartTime = Date.now();
    this.maxScrollDepth = 0;
    this.interactionCount = 0;
    this.scrollDepthThresholds = [25, 50, 75, 90, 100]; // Reset thresholds

    if (this.sessionData) {
      this.sessionData.page_views++;
      sessionStorage.setItem('analytics_session', JSON.stringify(this.sessionData));
    }

    this.setupEventListeners();
    this.startHeartbeat();
  }

  public stopPageTracking(): void {
    this.trackPageExit('visibility_change');
    this.stopHeartbeat();
    
    if (this.currentPageData) {
      this.currentPageData.exit_time = Date.now();
      this.currentPageData.scroll_depth = this.maxScrollDepth;
      this.currentPageData.interactions = this.interactionCount;
    }
  }

  public getSessionData(): SessionData | null {
    return this.sessionData;
  }

  public getCurrentPageData(): PageViewData | null {
    return this.currentPageData;
  }

  // Cleanup method
  public destroy(): void {
    this.stopHeartbeat();
    
    if (this.hasSetupListeners) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      window.removeEventListener('pagehide', this.handlePageHide.bind(this));
      window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
      
      ['click', 'keydown', 'scroll', 'mousemove'].forEach(eventType => {
        document.removeEventListener(eventType, this.handleUserInteraction.bind(this));
      });
      
      window.removeEventListener('scroll', this.trackScrollDepth.bind(this));
    }
  }
}

// Singleton instance
let pageTrackingManager: PageTrackingManager | null = null;

export const setupPageExitTracking = (): PageTrackingManager => {
  if (typeof window === 'undefined') {
    throw new Error('Page tracking can only be setup in browser environment');
  }

  if (!pageTrackingManager) {
    pageTrackingManager = new PageTrackingManager();
  }

  return pageTrackingManager;
};

export const getPageTracker = (): PageTrackingManager | null => {
  return pageTrackingManager;
};

// Utility functions for manual tracking
export const trackPageExit = (exitMethod: string = 'manual'): void => {
  if (pageTrackingManager) {
    pageTrackingManager['trackPageExit'](exitMethod as any);
  }
};

export const getCurrentSessionDuration = (): number => {
  const sessionData = pageTrackingManager?.getSessionData();
  if (sessionData) {
    return Date.now() - sessionData.start_time;
  }
  return 0;
};

export const getSessionId = (): string | null => {
  const sessionData = pageTrackingManager?.getSessionData();
  return sessionData?.session_id || null;
};